import { Reducer } from 'redux';
import omitUndefined from 'utils/omitUndefined';
import { StreamElementDataMessage } from 'communication/types/StreamElement';
import { WidgetActionType } from '../actions';
import {
    WidgetStreamAction,
    WidgetStreamMessageReceiveBatchPayload,
    WidgetStreamMessageReceivePayload,
} from './actions';
import { WidgetStreamState } from './WidgetStreamState';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import { StreamMessage } from 'utils/messages/StreamMessage';
import { getStreamErrorMessageText } from 'streamsState/store/streamStatuses/actions';

export function createStreamReducer<TWidgetDataState, TDataStreamElement>(
    initialState: WidgetStreamState<TWidgetDataState>,
    processDataStreamElement: (
        streamDataElement: TDataStreamElement,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void
): Reducer<
    WidgetStreamState<TWidgetDataState>,
    WidgetStreamAction<TDataStreamElement>
> {
    return (
        state: WidgetStreamState<TWidgetDataState> = initialState,
        action: WidgetStreamAction<TDataStreamElement>
    ) => {
        switch (action.type) {
            case WidgetActionType.WIDGET_RECEIVE_STREAM_MESSAGE_BATCH: {
                if (!action.payload || !action.payload.messages) {
                    return state;
                }

                if (
                    state.streamKey &&
                    state.streamKey !== action.payload.streamKey
                ) {
                    // Wrong stream: ignore..
                    return state;
                }

                return handleStreamMessageReceiveBatch(
                    omitUndefined({
                        ...state,
                        streamKey: !action.payload.allowMultiStream
                            ? action.payload.streamKey
                            : undefined,
                    }),
                    action.payload!,
                    processDataStreamElement
                );
            }

            case WidgetActionType.WIDGET_RECEIVE_STREAM_MESSAGE: {
                if (!action.payload || !action.payload.message) {
                    return state;
                }

                if (
                    state.streamKey &&
                    state.streamKey !== action.payload.streamKey
                ) {
                    // Wrong stream: ignore..
                    return state;
                }

                return handleStreamMessageReceive(
                    omitUndefined({
                        ...state,
                        streamKey: !action.payload.allowMultiStream
                            ? action.payload.streamKey
                            : undefined,
                    }),
                    action.payload!,
                    processDataStreamElement
                );
            }
            case WidgetActionType.WIDGET_CLEAR_STREAM_DATA: {
                return {
                    ...initialState,
                    streamKey: state.streamKey,
                };
            }
            case WidgetActionType.WIDGET_SET_STREAM_DATA: {
                /*
                 * Replaces the stream data completely. You will not normally use this. It was added for the support visualisation tool
                 */
                return {
                    ...initialState,
                    streamKey: state.streamKey,
                    data: action.payload!.data as TWidgetDataState,
                };
            }
            case WidgetActionType.WIDGET_WATCH_STREAM: {
                if (!action.payload) {
                    return state;
                }

                return {
                    ...state,
                    streamKey: !action.payload.allowMultiStream
                        ? action.payload.streamKey
                        : undefined,
                };
            }
            case WidgetActionType.WIDGET_HANDLE_STREAM_ERROR: {
                if (!action.payload || !action.payload.error) {
                    return state;
                }

                if (
                    state.streamKey &&
                    state.streamKey !== action.payload.streamKey
                ) {
                    // Wrong stream: ignore..
                    return state;
                }

                return {
                    ...state,
                    messages: ensureInvalidStreamMessage(
                        state.messages,
                        action.payload!.error
                    ),
                };
            }
        }
        return state;
    };
}

export function createProcessStreamDataMessage<
    TWidgetDataState,
    TDataStreamElement,
>(
    processDataStreamElement: (
        streamDataElement: TDataStreamElement,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void,
    additionalReducer: (
        streamDataElement: StreamElementDataMessage<TDataStreamElement>,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void = () => {
        return;
    }
) {
    return (
        streamDataElement: StreamElementDataMessage<TDataStreamElement>,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => {
        if (streamDataElement.data) {
            for (const element of streamDataElement.data) {
                processDataStreamElement(element, nextState, streamKey);
            }
        }
        additionalReducer(streamDataElement, nextState, streamKey);
    };
}

function handleStreamMessageReceiveBatch<TWidgetDataState, TDataStreamElement>(
    state: WidgetStreamState<TWidgetDataState>,
    payload: WidgetStreamMessageReceiveBatchPayload<TDataStreamElement>,
    processDataStreamElement: (
        streamDataElement: TDataStreamElement,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void
): WidgetStreamState<TWidgetDataState> {
    const nextState: WidgetStreamState<TWidgetDataState> = {
        ...state,
        data: { ...state.data },
    };

    for (const message of payload.messages) {
        processDataStreamElement(message, nextState, payload.streamKey);
    }
    return nextState;
}

function handleStreamMessageReceive<TWidgetDataState, TDataStreamElement>(
    state: WidgetStreamState<TWidgetDataState>,
    payload: WidgetStreamMessageReceivePayload<TDataStreamElement>,
    processDataStreamElement: (
        streamDataElement: TDataStreamElement,
        nextState: WidgetStreamState<TWidgetDataState>,
        streamKey: string
    ) => void
): WidgetStreamState<TWidgetDataState> {
    const nextState: WidgetStreamState<TWidgetDataState> = {
        ...state,
        data: { ...state.data },
    };

    processDataStreamElement(payload.message, nextState, payload.streamKey);
    return nextState;
}

export function ensureInvalidStreamMessage(
    messages: StreamMessage[],
    error: StreamError
) {
    const messageText = getStreamErrorMessageText(error);

    if (messages.find((m) => m.message === messageText)) {
        return messages;
    }

    return [
        {
            message: messageText,
        },
        ...messages,
    ];
}
